import { DelugeGame, diagonalCellBetween, diagonalTypeBetween } from './DelugeGame.js';
import { DelugeRenderer } from './DelugeRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Point } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, FinePoint } from '../../input/KeyboardController.js';
import { fineToCell } from '../../input/fineGeometry.js';

/** A half-grid intersection as a grid point (both coordinates are even there). */
function finePointToIntersection(p: FinePoint): Point {
    return { row: p.r / 2, col: p.c / 2 };
}

export class DelugeInput extends BaseInput<DelugeGame, DelugeRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: DelugeGame,
        renderer: DelugeRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const cell = this.detector.detectCell(x, y);
        if (cell && this.game.hasWall(cell.row, cell.col)) {
            this.game.removeWall(cell.row, cell.col);
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        if (this.gateMode && isVortexGame(this.game)) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.toggleVortexGate(cell)) {
                this.onUpdate();
            }
            return;
        }

        // Check for top rim edge (faucet placement)
        const edge = this.detector.detectEdge(x, y, 0.2, true);
        if (edge && edge.orient === 'h' && edge.pa.row === 0) {
            this.game.setFaucet(edge.pa.col);
            this.onUpdate();
            return;
        }

        // Check for cell click (for wall cycling)
        const cell = this.detector.detectCell(x, y);
        if (cell) {
            this.game.cycleWall(cell.row, cell.col);
            this.onUpdate();
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Deluge's walls are diagonals *inside* cells, joining two opposite corners — so
    // the cursor rides the half-grid and the trace anchors on intersections, like
    // Flood. But a diagonal isn't something you cross on the way: you steer to the
    // corner it ends at and it **snaps** in, Knight-style. Reaching one moves the
    // anchor there, so a zigzag pole is drawn in one gesture. The faucet is on the
    // top rim, as in Flood.

    usesFineCursor(): boolean {
        return true;
    }

    /** The faucet sits on the boundary, and diagonals reach the rim intersections. */
    allowsFineRim(): boolean {
        return true;
    }

    /** Water falls out of the grid rather than round to the other side. */
    wrapsFineCursor(): boolean {
        return false;
    }

    /** Diagonals join intersections, so that's where the trace anchors. */
    tracesFineIntersections(): boolean {
        return true;
    }

    /** The top rim is the faucet row — slide along it slot by slot. */
    fineTopRimSlots(): boolean {
        return true;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        // A tap on the top rim moves the faucet to that slot.
        if (p.r === 0 && p.c % 2 === 1) {
            this.game.setFaucet((p.c - 1) / 2);
            return true;
        }
        // A tap on a cell cycles its diagonal, as clicking it does.
        const cell = fineToCell(p);
        if (!cell) return false;
        this.game.cycleWall(cell.row, cell.col);
        return true;
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        const cell = fineToCell(p);
        if (!cell || !this.game.hasWall(cell.row, cell.col)) return false;
        this.game.removeWall(cell.row, cell.col);
        return true;
    }

    /** A fresh trace anchors here; the snap targets are drawn from it. */
    traceFineStart(p: FinePoint): void {
        if (this.gateMode) return;
        this.game.setTraceAnchor(finePointToIntersection(p));
    }

    /**
     * The cursor reached an intersection. If it's one of the anchor's diagonal
     * neighbours, toggle the diagonal joining them and re-anchor there — so the two
     * orthogonal steps it took to get there read as one diagonal, and tracing back
     * over a wall takes it away. Anywhere else is just navigation, and leaves the
     * anchor where it is.
     */
    traceFinePoint(to: FinePoint): boolean {
        if (this.gateMode) return false;
        const anchor = this.game.getTraceAnchor();
        if (!anchor) return false;

        const target = finePointToIntersection(to);
        const cell = diagonalCellBetween(anchor, target);
        if (!cell) return false;

        const changed = this.game.toggleDiagonal(
            cell.row, cell.col, diagonalTypeBetween(anchor, target)
        );
        this.game.setTraceAnchor(target);
        return changed;
    }

    /** Releasing drops the anchor, so the amber snap dots go away with the gesture. */
    traceEnd(): boolean {
        this.game.setTraceAnchor(null);
        return false;
    }

    /** `X` goes to the faucet — where the water starts. */
    fineCursorOrigin(): FinePoint {
        return { r: 0, c: 2 * this.game.getFaucetCol() + 1 };
    }
}
